import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
} from "@mui/material";
import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { useFirebaseApp, useFirestore } from "reactfire";
import SocialSignIn from "./SocialSignIn";

export default function CreateAccount({
  isOpen,
  handleClose,
}: {
  isOpen: boolean;
  handleClose: () => void;
}): React.ReactElement {
  const firebaseApp = useFirebaseApp();
  const firestore = useFirestore();
  const router = useRouter();

  const [state, setState] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    emailError: "",
    serverError: "",
    submitted: false,
    loading: false,
  });
  const { loading, submitted } = state;

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");

  const setters = {
    email: setEmailError,
    password: setPasswordError,
    firstName: setFirstNameError,
    lastName: setLastNameError,
  };

  const getValidation = (type: string, value: string) => {
    switch (type) {
      case "email":
        if (!value.length) return "Email is required";
        if (
          // eslint-disable-next-line no-useless-escape
          !/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
            value
          )
        )
          return "Email is not valid";
        return "";
      case "password":
        if (value.length === 0) return "Password is required";
        if (value.length < 6)
          return "Password must be at least 6 characters long";
        return "";
      case "firstName":
        return isEmpty(value) ? "First name is required" : "";
      case "lastName":
        return isEmpty(value) ? "Last name is required" : "";
      default:
        return "";
    }
  };

  const validateInput = (key: string, value: string) => {
    const error = getValidation(key, value);
    console.log("validateInput", key, value, error);
    setters[key] && setters[key](error);
    return error;
  };

  const isFormValid = () => {
    const { firstName, lastName, email, password } = state;

    const _emailError = validateInput("email", email);
    const emailValid = isEmpty(_emailError);

    const _pwdError = validateInput("password", password);
    const pwdValid = isEmpty(_pwdError);

    const _firstNameError = validateInput("firstName", firstName);
    const firstNameValid = isEmpty(_firstNameError);

    const _lastNameError = validateInput("lastName", lastName);
    const lastNameValid = isEmpty(_lastNameError);

    return emailValid && pwdValid && firstNameValid && lastNameValid;
  };

  const handleChange =
    (prop: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setState({ ...state, [prop]: event.target.value });
    };

  const handleError = (error: { code: string }) => {
    console.warn("error creating account", error);
    setState({ ...state, serverError: error.code, loading: false });
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e && e.preventDefault();
    const { firstName, lastName, email, password } = state;
    const newState = { ...state };

    if (isFormValid()) {
      setState({ ...newState, submitted: true, loading: true });
      try {
        await firebaseApp
          .auth()
          .createUserWithEmailAndPassword(email, password)
          .then(async (userCredential) => {
            const user = userCredential.user;
            if (user) {
              // create their profile
              const profileRef = firestore.collection("profiles").doc(user.uid);
              console.log("creating profile for user", { user });
              await profileRef.set(
                {
                  firstName,
                  lastName,
                  name: `${firstName} ${lastName}`,
                },
                { merge: true }
              );
              setState({ ...newState, loading: false });
              router.push("/onboarding/account");
            } else {
              console.error("expected valid user after login");
            }
          });
        const url = window.location.href;
        // TODO - config separately for dev/prod. probaly need to just disable
        // in dev since it currently doesn't have dynamic links setup.
        const verifyActionCode = {
          handleCodeInApp: true,
          url,
          iOS: {
            bundleId: "is.converge.converge",
          },
        };
        await firebaseApp.auth().currentUser?.sendEmailVerification();
        handleClose();
      } catch (err) {
        handleError(err);
      }
    } else {
      setState(newState);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSubmit();
  };

  const { serverError } = state;

  return (
    <Dialog
      open={isOpen}
      fullWidth={true}
      maxWidth="md"
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">Let's get started.</DialogTitle>
      <DialogContent>
        <Grid container alignItems="center" justifyContent="center">
          <Grid item md>
            <SocialSignIn />
          </Grid>
          <Divider sx={{ mx: 1 }} orientation="vertical" flexItem>
            <span style={{ color: "#999" }}>or</span>
          </Divider>

          <Grid item md={8}>
            <Grid container spacing={2}>
              <Grid item sm={6}>
                <TextField
                  onKeyPress={handleKeyPress}
                  error={firstNameError.length > 0}
                  helperText={firstNameError}
                  margin="normal"
                  required
                  autoFocus
                  id="firstName"
                  label="First Name"
                  type="firstName"
                  fullWidth
                  onChange={handleChange("firstName")}
                />
              </Grid>
              <Grid item sm={6}>
                <TextField
                  onKeyPress={handleKeyPress}
                  error={lastNameError.length > 0}
                  helperText={lastNameError}
                  margin="normal"
                  required
                  id="lastName"
                  label="Last Name"
                  type="firstName"
                  fullWidth
                  onChange={handleChange("lastName")}
                />
              </Grid>
            </Grid>

            <TextField
              onKeyPress={handleKeyPress}
              error={emailError.length > 0}
              helperText={emailError}
              margin="normal"
              required
              id="email"
              label="Email Address"
              type="email"
              fullWidth
              onChange={handleChange("email")}
            />

            <TextField
              onKeyPress={handleKeyPress}
              error={passwordError.length > 0}
              helperText={passwordError}
              margin="normal"
              required
              id="password"
              label="Password"
              type="password"
              autoComplete="Password"
              fullWidth
              onChange={handleChange("password")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button
          disabled={submitted || loading}
          onClick={handleSubmit}
          color="primary"
        >
          Create Account
        </Button>
      </DialogActions>

      {serverError.length > 0 && (
        <div className="server-error">{serverError}</div>
      )}
    </Dialog>
  );
}
